import { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  HStack,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import AvatarEditorModal from "./AvatarEditorModal";
import { CreatorsApi, type CreatorCreate } from "../api/creators";
import logger from "../analytics/logger";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const STATUSES = ["PROSPECTIVE", "VOUCHED", "ACTIVE"] as const;

const REQ_MARK = (
  <Text as="span" color="red.500" ml={1}>
    *
  </Text>
);

export default function AddCreatorModal({
  isOpen,
  onClose,
  onCreated,
}: Props) {
  const toast = useToast();
  const nameRef = useRef<HTMLInputElement | null>(null);
  const avatarDisclosure = useDisclosure();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [persona, setPersona] = useState("");
  const [ctype, setCtype] = useState("");
  const [origin, setOrigin] = useState("");
  const [status, setStatus] = useState<(typeof STATUSES)[number]>("PROSPECTIVE");
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; email?: string }>({});
  const [prevOpen, setPrevOpen] = useState(isOpen);

  const clearImage = () => {
    setImageBlob(null);
    setImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  if (isOpen !== prevOpen) {
    setPrevOpen(isOpen);
    if (!isOpen) {
      // Full reset whenever the modal closes — Save-and-add-another path
      // resets partially before close.
      setName("");
      setEmail("");
      setPersona("");
      setCtype("");
      setOrigin("");
      setStatus("PROSPECTIVE");
      clearImage();
      setError(null);
      setFieldErrors({});
      setBusy(false);
    }
  }

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const validate = () => {
    const errs: { name?: string; email?: string } = {};
    if (!name.trim()) errs.name = "Required";
    if (!email.trim()) errs.email = "Required";
    else if (!EMAIL_RE.test(email.trim())) errs.email = "Invalid email";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const doSave = async (): Promise<string | null> => {
    setError(null);
    if (!validate()) return null;
    setBusy(true);
    try {
      const body: CreatorCreate = {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        status,
        tagline: persona.trim() || null,
        creator_type: ctype.trim() || null,
        origin: origin.trim() || null,
      };
      const created = await CreatorsApi.create(body);
      if (imageBlob) {
        try {
          const { uploadUrl, key } = await CreatorsApi.getAvatarUploadUrl(
            created.id,
            "image/png",
          );
          const put = await fetch(uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": "image/png" },
            body: imageBlob,
          });
          if (!put.ok) throw new Error(`S3 upload failed (${put.status})`);
          // Attach via the gated PATCH (not a standalone endpoint) so the avatar
          // write is audited, same path the editor uses.
          await CreatorsApi.update(
            created.id,
            { profile_picture: key },
            { reason: "Profile picture set during creation" },
          );
        } catch (e) {
          logger.error("add_creator_avatar_failed", e);
          toast({
            status: "warning",
            title: "Creator added, but avatar upload failed",
            description: (e as { message?: string })?.message ?? "Avatar upload failed",
          });
        }
      }
      toast({ status: "success", title: `Added ${created.pii?.name || created.id}` });
      onCreated();
      return created.id;
    } catch (e) {
      logger.error("add_creator_failed", e);
      const err = e as {
        response?: { data?: { error?: string }; status?: number };
        message?: string;
      };
      const code = err?.response?.data?.error;
      if (err?.response?.status === 409 || code === "email_exists") {
        setError("A creator with this email already exists.");
      } else if (code) {
        setError(code);
      } else {
        setError(err?.message ?? "Failed to add creator");
      }
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    const id = await doSave();
    if (id) onClose();
  };

  const handleSaveAndAddAnother = async () => {
    const id = await doSave();
    if (!id) return;
    // Preserve origin, status; clear the rest.
    setName("");
    setEmail("");
    setPersona("");
    setCtype("");
    clearImage();
    setFieldErrors({});
    setError(null);
    setTimeout(() => nameRef.current?.focus(), 0);
  };

  const handleAvatarCommit = (blob: Blob) => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageBlob(blob);
    setImagePreview(URL.createObjectURL(blob));
    avatarDisclosure.onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => (busy ? null : onClose())}
      size="3xl"
      closeOnOverlayClick={!busy}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add creator</ModalHeader>
        <ModalCloseButton isDisabled={busy} />
        <ModalBody>
          {error && (
            <Alert status="error" mb={4}>
              <AlertIcon />
              {error}
            </Alert>
          )}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <Stack spacing={4}>
              <Box>
                <Text fontSize="sm" mb={1}>
                  Profile image
                </Text>
                <HStack>
                  {imagePreview ? (
                    <Image
                      src={imagePreview}
                      alt="avatar preview"
                      boxSize="64px"
                      borderRadius="full"
                      objectFit="cover"
                    />
                  ) : (
                    <Box
                      boxSize="64px"
                      borderRadius="full"
                      bg="gray.100"
                      border="1px dashed"
                      borderColor="gray.300"
                    />
                  )}
                  <Button size="sm" onClick={avatarDisclosure.onOpen} isDisabled={busy}>
                    {imagePreview ? "Replace image" : "Choose image"}
                  </Button>
                  {imagePreview && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={clearImage}
                      isDisabled={busy}
                    >
                      Remove
                    </Button>
                  )}
                </HStack>
              </Box>

              <Box>
                <Text fontSize="sm" mb={1}>
                  Origin
                </Text>
                <Input
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  isDisabled={busy}
                />
              </Box>

              <Box>
                <Text fontSize="sm" mb={1}>
                  Name{REQ_MARK}
                </Text>
                <Input
                  ref={nameRef}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  isDisabled={busy}
                  isInvalid={!!fieldErrors.name}
                  autoFocus
                />
                {fieldErrors.name && (
                  <Text color="red.500" fontSize="xs" mt={1}>
                    {fieldErrors.name}
                  </Text>
                )}
              </Box>

              <Box>
                <Text fontSize="sm" mb={1}>
                  Email{REQ_MARK}
                </Text>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  isDisabled={busy}
                  isInvalid={!!fieldErrors.email}
                />
                {fieldErrors.email && (
                  <Text color="red.500" fontSize="xs" mt={1}>
                    {fieldErrors.email}
                  </Text>
                )}
              </Box>
            </Stack>

            <Stack spacing={4}>
              <Box>
                <Text fontSize="sm" mb={1}>
                  Status
                </Text>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as (typeof STATUSES)[number])}
                  isDisabled={busy}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              </Box>

              <Box>
                <Text fontSize="sm" mb={1}>
                  Creator type
                </Text>
                <Input
                  value={ctype}
                  onChange={(e) => setCtype(e.target.value)}
                  isDisabled={busy}
                />
              </Box>

              <Box>
                <Text fontSize="sm" mb={1}>
                  Creator persona
                </Text>
                <Input
                  value={persona}
                  onChange={(e) => setPersona(e.target.value)}
                  isDisabled={busy}
                />
              </Box>
            </Stack>
          </SimpleGrid>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={busy}>
            Cancel
          </Button>
          <Button colorScheme="blue" mr={3} onClick={handleSave} isLoading={busy}>
            Save
          </Button>
          <Button colorScheme="blue" onClick={handleSaveAndAddAnother} isLoading={busy}>
            Save and add another
          </Button>
        </ModalFooter>
      </ModalContent>

      <AvatarEditorModal
        isOpen={avatarDisclosure.isOpen}
        onClose={avatarDisclosure.onClose}
        onCommit={handleAvatarCommit}
      />
    </Modal>
  );
}
